import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import Header from "../../Header";
import { Project } from "../../../types/admin";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  IconLookup,
  IconDefinition,
  findIconDefinition,
} from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { AdminApi } from "../../../Api/AdminApi";
import { useSortableTable } from "../../../hooks/useSortableTable";
import SortableHeader from "../../../Components/Table/sortableHeader";
import ProjectFilter, { ProjectFilters } from "../../../Components/Filters/ProjectFilter";
import { useAdmin } from "../../../Utils/AdminContext";
import ConfirmationModal from "../../../Components/Utils/ConfirmationModal";

library.add(faTrashCan);
const trashLookup: IconLookup = { prefix: "fas", iconName: "trash-can" };
const trashcanDefinition: IconDefinition = findIconDefinition(trashLookup);

function ListOfProjects() {
  const { instance, accounts } = useMsal();
  // Utilisation de useMemo pour mémoriser l'instance de l'API et éviter une réinstanciation à chaque rendu
  const api = useMemo(() => new AdminApi(accounts[0], instance), [accounts, instance]);
  const navigate = useNavigate();
  
  const { isAdmin, setIsAdmin } = useAdmin();
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/'); 
    }
  }, [isAdmin, navigate]);

  // State to store all unfiltered projects
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  // State to store current filters
  const [currentFilters, setCurrentFilters] = useState<ProjectFilters>({
    names: [],
    cordaStatuses: [],
    codes: [],
    statuses: []
  });

  // États pour la modal de confirmation
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  // Function to get questionnaire status label
  const getQuestionnaireStatusLabel = useCallback((status: number | null): string => {
    if (status === null) return "";
    
    const statusMapping = [
      "Opening",
      "Consortium editing",
      "Verification", 
      "Approval",
      "End"
    ];
    
    return statusMapping[status] || "";
  }, []);

  // Function to fetch all projects
  const fetchProjects = useCallback(async () => {
    try {
      const projectData = await api.getProjects();
      setAllProjects(projectData);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  }, [api]);

  const {
    sortedData: projects,
    setData,
    sortData,
    getSortIndicator,
  } = useSortableTable<Project>([], null); // Ne pas passer fetchProjects ici

  // Effet pour charger les projets au montage du composant
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Function to apply filters with exact matches
  const applyFilters = useCallback((projects: Project[], filters: ProjectFilters) => {
    let result = [...projects];
    
    // Filter by names - exact match
    if (filters.names.length > 0) {
      result = result.filter(project => 
        project.name && filters.names.includes(project.name)
      );
    }
    
    // Filter by Corda statuses - exact match
    if (filters.cordaStatuses.length > 0) {
      result = result.filter(project => 
        project.cordaStatus && filters.cordaStatuses.includes(project.cordaStatus)
      );
    }
    
    // Filter by codes - exact match
    if (filters.codes.length > 0) {
      result = result.filter(project => 
        project.code && filters.codes.includes(project.code)
      );
    }
    
    // Filter by project status (questionnaireStatus) - exact match
    if (filters.statuses.length > 0) {
      result = result.filter(project => 
        project.questionnaireStatus !== null && 
        filters.statuses.includes(project.questionnaireStatus.toString())
      );
    }
    
    return result;
  }, []); // Pas de dépendances ici car cette fonction ne dépend d'aucun état

  // Effect to apply filters when they change
  useEffect(() => {
    // Éviter l'exécution inutile si les projets ne sont pas encore chargés
    if (allProjects.length === 0) return;
    
    const filtered = applyFilters(allProjects, currentFilters);
    setData(filtered);
  }, [allProjects, currentFilters, applyFilters, setData]);

  // Handler for filter changes
  const handleFiltersChange = useCallback((filters: ProjectFilters) => {
    setCurrentFilters(filters);
  }, []);

  const handleDeleteProject = useCallback((id: string) => {
    setProjectToDelete(id);
    setIsModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (projectToDelete) {
      try {
        await api.deleteProject(projectToDelete);
        setIsModalOpen(false);
        setProjectToDelete(null);
        // Refresh the list after deletion
        fetchProjects();
      } catch (error) {
        console.error("Error deleting project:", error);
        setIsModalOpen(false);
        setProjectToDelete(null);
      }
    }
  }, [api, fetchProjects, projectToDelete]);

  const handleExportCsv = async () => {
    try {
      await api.exportProjectsCsv();
    } catch (error) {
      console.error("Error exporting users to CSV:", error);
    } 
  };

  return (
    <>
      <Header projectName="" loaded={false} isAdmin={true}/>
      <div className="project-container">
        <div className="button-holder">
          <input
            className="big-button mx-auto mt-3"
            type="submit"
            value="Export csv"
            onClick={handleExportCsv}
          />
        </div>
        <div className="feedstock-container">
          <h1 className="page-title">Projects</h1>
          
          <ProjectFilter onFiltersChange={handleFiltersChange} />
          
          <table className="table mt-5">
            <thead>
              <tr>
                <SortableHeader<Project>
                  label="Programme"
                  sortKey="isCbe"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Project>
                  label="Project acronym"
                  sortKey="name"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Project>
                  label="Project number"
                  sortKey="code"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Project>
                  label="CORDA status"
                  sortKey="cordaStatus"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Project>
                  label="Questionnaire status"
                  sortKey="questionnaireStatus"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Project>
                  label="Actions"
                  sortKey="id"
                  sortData={sortData}
                  getSortIndicator={getSortIndicator}
                  isAction={true}
                />
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr key={index}>
                  <td>{project.isCbe ? "CBE" : "BBI"}</td>
                  <td>{project.name}</td>
                  <td>{project.code}</td>
                  <td>{project.cordaStatus}</td>
                  <td>{getQuestionnaireStatusLabel(project.questionnaireStatus)}</td>
                  <td>
                    <a
                      className="action-button"
                      href={`Project/${project.id}`}
                    >
                      <span>Edit project</span>
                    </a>
                    <a
                      className="action-button delete-button"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      <FontAwesomeIcon icon={trashcanDefinition} />
                      <span>Delete project</span>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <ConfirmationModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          onConfirm={confirmDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this project? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          requireComment={false}
        />
      </div>
    </>
  );
}

export default ListOfProjects;